import { defineStore } from 'pinia'
import { wxLogin } from '@/api/wechat'
import { carrierLogin } from '@/api/carrierAuth'
import { mobileLogin } from '@/api/mobileAuth'
import { checkToken } from '@/api/user'

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('token') || null,
    userInfo: JSON.parse(localStorage.getItem('user') || 'null'),
    lastLoginTime: localStorage.getItem('lastLoginTime') || null,
    isInitComplete: false
  }),
  getters: {
    safeUserInfo: (state) => state.userInfo || {},
    hasAvatar: (state) => !!state.userInfo?.avatar,
    isAuthenticated: (state) => !!state.token && !state.isTokenExpired,
    hasMobile: (state) => !!(state.userInfo?.mobile || state.userInfo?.mobilePartial),
    isTokenExpired: (state) => {
      if (!state.lastLoginTime) return true
      return Date.now() - new Date(state.lastLoginTime).getTime() > 7 * 24 * 60 * 60 * 1000
    }
  },
  actions: {
    // 微信登录
    async wxUserLogin(code) {
      const { token, nickname, avatar, status } = await wxLogin(code)

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.token = token
      this.userInfo = { nickname, avatar }
      this.lastLoginTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastLoginTime)

      return { success: true }
    },

    // 运营商一键登录
    async carrierUserLogin(carrierToken, carrierType) {
      const result = await carrierLogin({
        carrierToken,
        carrierType
      })
      const { token, nickname, avatar, status } = result

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.token = token
      this.userInfo = { nickname, avatar }
      this.lastLoginTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastLoginTime)

      return { success: true }
    },

    // 手机号验证码登录
    async mobileUserLogin(mobile, code) {
      try {
        const result = await mobileLogin({ mobile, code });
        const { token, nickname, avatar, status } = result;

        // 0:禁用, 1:正常, 2:关闭
        if (status !== 1) {
          const statusMsg = status === 0 ? '禁用' : '关闭';
          return {
            success: false,
            status: status,
            message: `帐号已${statusMsg}，请联系平台`
          };
        }

        // 保存手机号脱敏信息
        const mobilePartial = mobile.substring(0, 3) + "****" + mobile.substring(mobile.length - 4);

        this.token = token;
        this.userInfo = { nickname, avatar, mobilePartial };
        this.lastLoginTime = new Date().toISOString();

        // 使用localStorage持久化存储
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify({
          nickname,
          avatar,
          mobilePartial,
        }));
        localStorage.setItem('lastLoginTime', this.lastLoginTime);

        return { success: true };
      } catch (error) {
        // API 返回错误（如验证码过期、验证码错误等）
        console.error('手机号登录API错误:', error);
        return {
          success: false,
          isApiError: true,
          message: error.message || '登录失败，请重试'
        };
      }
    },

    // 初始化store
    async init() {
      try {
        if (this.token && !this.isTokenExpired) {
          const isValid = await checkToken(this.token)
          console.log("checkToken:", isValid);
          if (isValid != true) this.clearAuth()
        } else {
          this.clearAuth()
        }
      } catch (error) {
        console.error('Token验证失败:', error)
        this.clearAuth()
      } finally {
        this.isInitComplete = true
      }
    },
    // 清除认证信息
    clearAuth() {
      this.$reset()
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('lastLoginTime')
    },
    // 退出登录
    logout() {
      this.clearAuth()
    },

    // ========== 新增：更新用户信息的 action ==========
    updateUserInfo(newInfo) {
      // 合并新信息到原始 userInfo（保持响应式）
      this.userInfo = {
        ...this.userInfo,
        ...newInfo
      };
      // 同步更新本地存储（与仓库状态一致）
      localStorage.setItem('user', JSON.stringify(this.userInfo));
    }
  }
})